/**
 * Nosto-ProductCard.js
 *
 * Solicita una vista Liquid de producto, la inserta dentro de un
 * placeholder e inicializa las interacciones del product card.
 *
 * Administra el carrusel Flickity, los cambios de color, los enlaces,
 * precios, wishlist y la apertura del modal de compra rápida.
 *
 * Este archivo no ejecuta búsquedas de Nosto, no consulta GraphQL,
 * no agrega productos directamente al carrito y no construye el HTML
 * principal del card.
 */

(function initializeNostoProductCard(global) {
    "use strict";

    class NostoProductCard {
        /**
         * Crea una instancia para renderizar e inicializar un product card.
         */
        constructor({
            hit = null,
            productId = "",
            container = null,
            containerId = "",
            context = {},
            source = "",
            cardType = "normal",
            track = "",
            attributionKey = "",
            viewService = null,
            viewName = "",
            quickBuyViewName = "nosto-quick-buy",
            onProductClick = null,
            onQuickBuyOpen = null,
            onColorChange = null,
            onRendered = null,
            onError = null,
            debug = false,
        } = {}) {
            this.hit = hit;

            this.productId =
                this.normalizeProductId(
                    productId ||
                    hit?.productId ||
                    hit?.product_id ||
                    hit?.id
                );

            this.container =
                this.resolveElement(
                    container ||
                    containerId
                );

            this.context = {
                ...context,
            };

            this.cardType =
                this.normalizeCardType(
                    cardType ||
                    context?.cardType
                );

            this.source =
                this.normalizeString(
                    source ||
                    context?.source
                );

            this.track =
                this.normalizeString(
                    track ||
                    context?.track
                );

            this.attributionKey =
                this.normalizeString(
                    attributionKey ||
                    context?.attributionKey
                );

            this.viewService =
                viewService;

            this.viewName =
                this.normalizeString(
                    viewName
                ) ||
                (
                    this.cardType === "editorial"
                        ? "nosto-card-editorial"
                        : (
                            this.normalizeString(
                                this.viewService?.cardView
                            ) ||
                            "nosto-card"
                        )
                );

            this.quickBuyViewName =
                this.normalizeString(
                    quickBuyViewName
                ) || "nosto-quick-buy";

            this.onProductClick =
                typeof onProductClick ===
                    "function"
                    ? onProductClick
                    : null;

            this.onQuickBuyOpen =
                typeof onQuickBuyOpen ===
                    "function"
                    ? onQuickBuyOpen
                    : null;

            this.onColorChange =
                typeof onColorChange ===
                    "function"
                    ? onColorChange
                    : null;

            this.onRendered =
                typeof onRendered ===
                    "function"
                    ? onRendered
                    : null;

            this.onError =
                typeof onError === "function"
                    ? onError
                    : null;

            this.debug =
                Boolean(debug);

            this.root = null;
            this.activeMediaGroup = null;
            this.activeFlickity = null;
            this.swipeHintHasPlayed = false;
            this.swipeHintAnimationFrame = null;
            this.swipeHintTimeout = null;

            this.flickityByGroup =
                new Map();

            this.isRendering = false;
            this.isRendered = false;
            this.isDestroyed = false;

            this.renderPromise = null;

            this.boundHandleClick =
                this.handleClick.bind(this);

            this.boundHandleKeydown =
                this.handleKeydown.bind(this);

            this.boundHandleResize =
                this.handleResize.bind(this);
        }

        /**
         * Escribe mensajes en consola cuando el modo debug está activo.
         */
        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoProductCard]",
                ...args
            );
        }

        /**
         * Convierte un valor en una cadena limpia.
         */
        normalizeString(value) {
            return String(value ?? "").trim();
        }

        /**
 * Normaliza la variante visual del product card.
 */
        normalizeCardType(value) {
            const normalizedType =
                this.normalizeString(value)
                    .toLowerCase();

            const allowedTypes = [
                "normal",
                "editorial",
                "featured",
            ];

            return allowedTypes.includes(
                normalizedType
            )
                ? normalizedType
                : "normal";
        }

        /**
         * Normaliza un ID numérico o Global ID de Shopify.
         */
        normalizeProductId(productId) {
            const normalizedId =
                this.normalizeString(productId);

            if (!normalizedId) {
                return "";
            }

            return normalizedId
                .split("/")
                .filter(Boolean)
                .at(-1);
        }
        /**
 * Indica si este card debe mostrar
 * la demostración de swipe.
 */
        shouldPlayMobileSwipeHint() {
            if (
                this.swipeHintHasPlayed ||
                this.isEditorial() ||
                !global.matchMedia(
                    "(max-width: 749px)"
                ).matches
            ) {
                return false;
            }

            if (
                !this.root ||
                !this.activeFlickity ||
                !this.activeMediaGroup
            ) {
                return false;
            }

            const resultsContainer =
                this.root.closest(
                    [
                        "[data-nosto-plp-results]",
                        "[data-nosto-search-results]",
                    ].join(",")
                );

            if (!resultsContainer) {
                return false;
            }

            const currentItem =
                this.root.closest(
                    ".grid-item"
                );

            const firstItem =
                resultsContainer.querySelector(
                    ".grid-item"
                );

            if (
                !currentItem ||
                currentItem !== firstItem
            ) {
                return false;
            }

            return (
                this.getSlides(
                    this.activeMediaGroup
                ).length > 1
            );
        }


        /**
         * Muestra media segunda imagen y regresa
         * a la primera para indicar que existe swipe.
         */
        playMobileSwipeHint() {
            if (
                !this.shouldPlayMobileSwipeHint()
            ) {
                return;
            }

            const instance =
                this.activeFlickity;

            const slider =
                instance?.slider;

            if (!slider) {
                return;
            }

            const observer =
                new IntersectionObserver(
                    (entries) => {
                        const entry =
                            entries[0];

                        if (
                            !entry?.isIntersecting
                        ) {
                            return;
                        }

                        observer.disconnect();

                        if (
                            this.swipeHintHasPlayed ||
                            this.isEditorial() ||
                            this.activeFlickity !== instance
                        ) {
                            return;
                        }

                        this.swipeHintHasPlayed =
                            true;

                        this.swipeHintTimeout =
                            global.setTimeout(
                                () => {
                                    if (
                                        !this.root?.isConnected ||
                                        this.activeFlickity !== instance
                                    ) {
                                        return;
                                    }

                                    const viewport =
                                        slider.parentElement;

                                    const cardWidth =
                                        viewport
                                            ?.getBoundingClientRect()
                                            .width;

                                    if (!cardWidth) {
                                        return;
                                    }

                                    /*
                                     * Transform actual de Flickity.
                                     */
                                    const computedTransform =
                                        global
                                            .getComputedStyle(
                                                slider
                                            )
                                            .transform;

                                    /*
                                     * Conservamos exactamente la posición
                                     * que Flickity ya tiene.
                                     */
                                    const startMatrix =
                                        new DOMMatrix(
                                            computedTransform === "none"
                                                ? undefined
                                                : computedTransform
                                        );

                                    /*
                                     * Copiamos esa misma matriz y únicamente
                                     * la desplazamos media card a la izquierda.
                                     */
                                    const hintMatrix =
                                        new DOMMatrix(
                                            startMatrix.toString()
                                        );

                                    hintMatrix.e -=
                                        cardWidth * 0.5;

                                    const animation =
                                        slider.animate(
                                            [
                                                {
                                                    transform:
                                                        startMatrix.toString(),
                                                },
                                                {
                                                    transform:
                                                        hintMatrix.toString(),
                                                    offset: 0.45,
                                                },
                                                {
                                                    transform:
                                                        startMatrix.toString(),
                                                },
                                            ],
                                            {
                                                duration: 1200,
                                                easing:
                                                    "ease-in-out",
                                            }
                                        );

                                    animation.onfinish =
                                        () => {
                                            instance
                                                .positionSlider?.();
                                        };

                                    animation.onfinish =
                                        () => {
                                            instance
                                                .positionSlider?.();
                                        };
                                },
                                700
                            );
                    },
                    {
                        threshold: 0.4,
                    }
                );

            observer.observe(
                this.root
            );
        }
        /**
         * Resuelve un selector, ID o elemento HTML.
         */
        resolveElement(target) {
            if (target instanceof Element) {
                return target;
            }

            if (typeof target !== "string") {
                return null;
            }

            const normalizedTarget =
                target.trim();

            if (!normalizedTarget) {
                return null;
            }

            return (
                document.querySelector(
                    normalizedTarget
                ) ||
                document.getElementById(
                    normalizedTarget
                )
            );
        }

        /**
         * Valida que el card tenga las dependencias necesarias.
         */
        validateDependencies() {
            if (!this.container) {
                throw new Error(
                    "NostoProductCard requiere un contenedor válido."
                );
            }

            if (!this.hit) {
                throw new Error(
                    "NostoProductCard requiere el hit original de Nosto."
                );
            }

            if (!this.viewService) {
                throw new Error(
                    "NostoProductCard requiere una instancia de NostoProductViewService."
                );
            }

            if (
                typeof this.viewService
                    .getCard !== "function"
            ) {
                throw new TypeError(
                    "NostoProductViewService debe exponer el método getCard()."
                );
            }
        }

        /**
         * Obtiene el handle del producto desde el hit de Nosto.
         */
        getHandle() {
            if (
                typeof this.viewService
                    ?.getHandleFromHit ===
                "function"
            ) {
                return this.viewService
                    .getHandleFromHit(
                        this.hit
                    );
            }

            const url =
                this.normalizeString(
                    this.hit?.url
                );

            if (!url) {
                return "";
            }

            try {
                const parsedUrl =
                    new URL(
                        url,
                        global.location.origin
                    );

                const pathParts =
                    parsedUrl.pathname
                        .split("/")
                        .filter(Boolean);

                const productsIndex =
                    pathParts.indexOf(
                        "products"
                    );

                return productsIndex >= 0
                    ? pathParts[
                    productsIndex + 1
                    ] || ""
                    : pathParts.at(-1) || "";
            } catch (error) {
                return url
                    .split("/products/")
                    .at(-1)
                    ?.split("?")[0]
                    ?.replace(/\/$/, "") ||
                    "";
            }
        }

        /**
     * Solicita el HTML Liquid e inicializa el product card.
     * Las llamadas simultáneas reutilizan la misma promesa.
     */
        async render() {
            if (this.isRendered) {
                return this.root;
            }

            if (this.renderPromise) {
                return this.renderPromise;
            }

            this.validateDependencies();

            this.renderPromise =
                this.performRender();

            try {
                return await this.renderPromise;
            } finally {
                this.renderPromise = null;
            }
        }

        /**
         * Ejecuta internamente la carga e inicialización del product card.
         */
        async performRender() {
            const handle =
                this.getHandle();

            if (!handle) {
                throw new Error(
                    `No fue posible obtener el handle del producto ${this.productId}.`
                );
            }

            this.isRendering = true;
            this.setLoading(true);

            try {
                const html =
                    await this.viewService
                        .getCard({
                            hit: this.hit,
                            handle,
                            productId:
                                this.productId,
                            view:
                                this.viewName,
                            context:
                                this.context,
                        });

                if (this.isDestroyed) {
                    return null;
                }

                this.insertHtml(html);
                this.root.__nostoProductCardInstance = this;
                this.initialize();

                this.isRendered = true;

                this.onRendered?.({
                    card: this,
                    root: this.root,
                    hit: this.hit,
                    productId:
                        this.productId,
                    context:
                        this.context,
                });

                this.log(
                    "Product card renderizado.",
                    {
                        productId:
                            this.productId,
                        handle,
                    }
                );

                return this.root;
            } catch (error) {
                this.renderError(error);

                this.onError?.({
                    error,
                    card: this,
                    hit: this.hit,
                    productId:
                        this.productId,
                });

                throw error;
            } finally {
                this.isRendering = false;
                this.setLoading(false);
            }
        }

        /**
 * Inserta el HTML recibido y localiza el elemento raíz del card.
 */
        insertHtml(html) {
            const normalizedHtml =
                this.normalizeString(html);

            if (!normalizedHtml) {
                throw new Error(
                    `La vista del producto ${this.productId} devolvió HTML vacío.`
                );
            }

            const template =
                document.createElement(
                    "template"
                );

            template.innerHTML =
                normalizedHtml;

            const cardRoot =
                template.content.querySelector(
                    "[data-nosto-product-card]"
                );

            if (!cardRoot) {
                throw new Error(
                    "La vista Liquid no contiene [data-nosto-product-card]."
                );
            }

            /*
             * La vista Liquid es la fuente definitiva del tipo de card.
             * Evita que el valor inicial "normal" sobrescriba una respuesta
             * renderizada como editorial.
             */


            this.container.replaceChildren(
                template.content
            );

            this.root =
                this.container.querySelector(
                    "[data-nosto-product-card]"
                );

            if (!this.root) {
                throw new Error(
                    "No fue posible insertar el product card."
                );
            }

            this.applyCardType();

            this.root.dataset.nostoSource =
                this.source;

            this.root.dataset.nostoTrack =
                this.track;

            this.root.dataset
                .nostoOriginalProductId =
                this.productId;

            if (this.attributionKey) {
                this.root.dataset
                    .nostoAttribution =
                    this.attributionKey;
            }
        }

        /**
 * Cambia la variante visual de una card ya renderizada.
 */
        setCardType(cardType) {
            const nextCardType =
                this.normalizeCardType(
                    cardType
                );

            if (
                this.cardType ===
                nextCardType
            ) {
                return this.cardType;
            }

            this.cardType =
                nextCardType;

            this.applyCardType();

            this.syncMediaMode({
                resetPosition: true,
            });

            return this.cardType;
        }

        /**
 * Aplica la variante visual y semántica del product card.
 */
        applyCardType() {
            if (!this.root) {
                return;
            }

            const isNormal =
                this.cardType === "normal";

            const isEditorial =
                this.cardType === "editorial";

            const isFeatured =
                this.cardType === "featured";

            this.root.dataset.cardType =
                this.cardType;

            this.root.classList.toggle(
                "nosto-product-card--normal",
                isNormal
            );

            this.root.classList.toggle(
                "nosto-product-card--editorial",
                isEditorial
            );

            this.root.classList.toggle(
                "nosto-product-card--featured",
                isFeatured
            );
        }

        /**
 * Oculta los badges en vista editorial.
 * En vista normal restaura el badge del producto activo.
 */
        syncBadgeVisibility() {
            const badgeContainer =
                this.root?.querySelector(
                    "[data-nosto-card-badge]"
                );

            if (!badgeContainer) {
                return;
            }

            if (this.isEditorial()) {
                badgeContainer.hidden = true;
                return;
            }

            const activeProductId =
                this.normalizeProductId(
                    this.root.dataset.activeProductId ||
                    this.productId
                );

            this.updateBadge(
                activeProductId
            );
        }

        /**
         * Inicializa eventos, carrusel y estado del card insertado.
         */
        initialize() {
            if (!this.root) {
                return;
            }

            this.applyTrackingToExistingLinks();

            this.root.addEventListener(
                "click",
                this.boundHandleClick
            );

            this.root.addEventListener(
                "keydown",
                this.boundHandleKeydown
            );

            document.addEventListener(
                "theme:resize:width",
                this.boundHandleResize
            );

            this.activeMediaGroup =
                this.getActiveMediaGroup() ||
                this.root.querySelector(
                    "[data-nosto-card-media-group]"
                );

            if (this.activeMediaGroup) {
                this.activateMediaGroup(
                    this.activeMediaGroup,
                    {
                        resetPosition: true,
                    }
                );
            }

            this.syncInitialState();
        }

        /**
         * Sincroniza el card con el swatch marcado inicialmente.
         */
        syncInitialState() {
            const activeSwatch =
                this.root?.querySelector(
                    "[data-nosto-card-swatch].is-active"
                ) ||
                this.root?.querySelector(
                    "[data-nosto-card-swatch]"
                );

            if (!activeSwatch) {
                return;
            }

            this.updateCardFromSwatch(
                activeSwatch,
                {
                    changeMediaGroup: false,
                    emit: false,
                }
            );
        }

        /**
         * Comprueba que Flickity esté disponible en el theme.
         */
        getFlickityClass() {
            const FlickityClass =
                global.theme?.Flickity ||
                global.Flickity;

            if (
                typeof FlickityClass !==
                "function"
            ) {
                throw new Error(
                    "Flickity no está disponible en window.theme.Flickity."
                );
            }

            return FlickityClass;
        }

        /**
         * Obtiene el grupo de imágenes que se encuentra activo.
         */
        getActiveMediaGroup() {
            return (
                this.root?.querySelector(
                    "[data-nosto-card-media-group].is-active"
                ) || null
            );
        }

        /**
         * Obtiene el track de imágenes dentro de un grupo.
         */
        getMediaTrack(group) {
            return (
                group?.querySelector(
                    "[data-nosto-card-track]"
                ) || null
            );
        }

        /**
         * Obtiene las imágenes disponibles dentro de un grupo.
         */
        getSlides(group) {
            return Array.from(
                group?.querySelectorAll(
                    "[data-nosto-card-slide]"
                ) || []
            );
        }

        /**
 * Obtiene el índice inicial de imagen configurado en Liquid.
 *
 * data-initial-media-position usa posiciones humanas:
 * 1 = primera imagen
 * 2 = segunda imagen
 * 3 = tercera imagen
 *
 * Internamente los arrays/Flickity utilizan índices desde 0.
 *
 * Si la posición solicitada supera la cantidad de imágenes,
 * devuelve el índice de la última disponible.
 */
        getInitialMediaIndex(group) {
            const slides =
                this.getSlides(group);

            if (!slides.length) {
                return 0;
            }

            const requestedPosition =
                Number(
                    this.root?.dataset
                        .initialMediaPosition ||
                    1
                );

            const normalizedPosition =
                Number.isFinite(requestedPosition)
                    ? Math.max(
                        1,
                        Math.floor(requestedPosition)
                    )
                    : 1;

            return Math.min(
                normalizedPosition - 1,
                slides.length - 1
            );
        }

        /**
         * Crea una instancia de Flickity para un grupo de imágenes.
         */
        createFlickity(group) {
            if (
                !group
            ) {
                return null;
            }

            const existingInstance =
                this.flickityByGroup.get(
                    group
                );

            if (existingInstance) {
                return existingInstance;
            }

            const track =
                this.getMediaTrack(group);

            const slides =
                this.getSlides(group);

            if (
                !track ||
                slides.length <= 1
            ) {
                this.updateCarouselControls(
                    group,
                    null
                );

                return null;
            }

            const FlickityClass =
                this.getFlickityClass();

            const instance =
                new FlickityClass(
                    track,
                    {
                        cellSelector:
                            "[data-nosto-card-slide]",

                        cellAlign: "left",
                        contain: true,
                        wrapAround: true,
                        draggable: true,
                        adaptiveHeight: true,
                        imagesLoaded: true,

                        prevNextButtons: false,
                        pageDots: false,

                        accessibility: true,
                        setGallerySize: true,
                        percentPosition: false,
                    }
                );

            instance.on(
                "select",
                () => {
                    this.handleFlickitySelect(
                        group,
                        instance
                    );
                }
            );

            instance.on(
                "settle",
                () => {
                    this.updateSlideAccessibility(
                        group,
                        instance
                    );
                }
            );

            this.flickityByGroup.set(
                group,
                instance
            );

            this.updateCarouselControls(
                group,
                instance
            );

            this.handleFlickitySelect(
                group,
                instance
            );

            return instance;
        }

        /**
 * Activa un grupo de imágenes y prepara su comportamiento
 * dependiendo del tipo de card.
 */
        activateMediaGroup(
            nextGroup,
            {
                resetPosition = true,
            } = {}
        ) {
            if (!nextGroup) {
                return null;
            }

            this.root
                .querySelectorAll(
                    "[data-nosto-card-media-group]"
                )
                .forEach((group) => {
                    const isActive =
                        group === nextGroup;

                    group.hidden =
                        !isActive;

                    group.classList.toggle(
                        "is-active",
                        isActive
                    );

                    this.updateGroupLinksTabIndex(
                        group,
                        isActive
                    );
                });

            this.activeMediaGroup =
                nextGroup;

            return this.syncMediaMode({
                resetPosition,
            });
        }

        /**
         * Actualiza la accesibilidad de los enlaces de un grupo visible u oculto.
         */
        updateGroupLinksTabIndex(
            group,
            isActive
        ) {
            group
                .querySelectorAll(
                    "a, button, input, select"
                )
                .forEach((element) => {
                    if (isActive) {
                        element.removeAttribute(
                            "tabindex"
                        );
                    } else {
                        element.setAttribute(
                            "tabindex",
                            "-1"
                        );
                    }
                });
        }

        /**
         * Reacciona al cambio de slide de Flickity.
         */
        handleFlickitySelect(
            group,
            instance
        ) {
            const selectedIndex =
                Number(
                    instance?.selectedIndex ||
                    0
                );

            this.updatePagination(
                group,
                selectedIndex
            );

            this.updateSlideAccessibility(
                group,
                instance
            );
        }

        /**
         * Actualiza los indicadores visuales del carrusel.
         */
        updatePagination(
            group,
            selectedIndex
        ) {
            group
                ?.querySelectorAll(
                    "[data-nosto-card-pagination-dot]"
                )
                .forEach(
                    (
                        dot,
                        index
                    ) => {
                        dot.classList.toggle(
                            "is-active",
                            index === selectedIndex
                        );
                    }
                );
        }

        /**
         * Actualiza tabindex y aria-hidden de los slides.
         */
        updateSlideAccessibility(
            group,
            instance
        ) {
            const selectedIndex =
                Number(
                    instance?.selectedIndex ||
                    0
                );

            this.getSlides(group)
                .forEach(
                    (
                        slide,
                        index
                    ) => {
                        const isActive =
                            index === selectedIndex;

                        slide.setAttribute(
                            "aria-hidden",
                            String(!isActive)
                        );

                        slide
                            .querySelectorAll(
                                "a, button"
                            )
                            .forEach(
                                (element) => {
                                    element.tabIndex =
                                        isActive
                                            ? 0
                                            : -1;
                                }
                            );
                    }
                );
        }

        /**
         * Actualiza la visibilidad de flechas y paginación.
         */
        updateCarouselControls(
            group,
            instance
        ) {
            const slideCount =
                this.getSlides(group)
                    .length;

            const hasMultipleSlides =
                slideCount > 1 &&
                Boolean(instance) &&
                !this.isEditorial();

            const previousButton =
                this.root?.querySelector(
                    "[data-nosto-card-prev]"
                );

            const nextButton =
                this.root?.querySelector(
                    "[data-nosto-card-next]"
                );

            if (previousButton) {
                previousButton.hidden =
                    !hasMultipleSlides;

                previousButton.setAttribute(
                    "aria-hidden",
                    String(!hasMultipleSlides)
                );
            }

            if (nextButton) {
                nextButton.hidden =
                    !hasMultipleSlides;

                nextButton.setAttribute(
                    "aria-hidden",
                    String(!hasMultipleSlides)
                );
            }

            const pagination =
                group?.querySelector(
                    "[data-nosto-card-pagination]"
                );

            if (pagination) {
                pagination.hidden =
                    !hasMultipleSlides;

                pagination.setAttribute(
                    "aria-hidden",
                    String(!hasMultipleSlides)
                );
            }
        }

        /**
         * Reacciona a todos los clics delegados del card.
         */
        handleClick(event) {
            const swatch =
                event.target.closest(
                    "[data-nosto-card-swatch]"
                );

            if (
                swatch &&
                this.root.contains(
                    swatch
                )
            ) {
                event.preventDefault();

                this.selectColor(
                    swatch
                );

                return;
            }

            const previousButton =
                event.target.closest(
                    "[data-nosto-card-prev]"
                );

            if (previousButton) {
                event.preventDefault();
                event.stopPropagation();

                if (this.isEditorial()) {
                    return;
                }

                this.activeFlickity
                    ?.previous(true);

                return;
            }

            const nextButton =
                event.target.closest(
                    "[data-nosto-card-next]"
                );

            if (nextButton) {
                event.preventDefault();
                event.stopPropagation();

                if (this.isEditorial()) {
                    return;
                }

                this.activeFlickity
                    ?.next(true);

                return;
            }

            const paginationDot =
                event.target.closest(
                    "[data-nosto-card-pagination-dot]"
                );

            if (paginationDot) {
                event.preventDefault();

                if (this.isEditorial()) {
                    return;
                }

                const index =
                    Number(
                        paginationDot.dataset
                            .slideIndex
                    );

                if (Number.isFinite(index)) {
                    this.activeFlickity
                        ?.select(index);
                }

                return;
            }

            const quickAddButton =
                event.target.closest(
                    "[data-nosto-card-quick-add]"
                );

            if (quickAddButton) {
                event.preventDefault();

                this.openQuickBuy(
                    quickAddButton
                );

                return;
            }

            const productLink =
                event.target.closest(
                    "[data-nosto-card-link], [data-nosto-card-media-group] a"
                );

            if (productLink) {
                this.recordProductClick(
                    productLink
                );
            }
        }

        /**
         * Permite navegar con teclado entre los swatches de color.
         */
        handleKeydown(event) {
            const swatch =
                event.target.closest(
                    "[data-nosto-card-swatch]"
                );

            if (!swatch) {
                return;
            }

            if (
                event.key !==
                "ArrowLeft" &&
                event.key !==
                "ArrowRight"
            ) {
                return;
            }

            event.preventDefault();

            const swatches =
                Array.from(
                    this.root.querySelectorAll(
                        "[data-nosto-card-swatch]:not([disabled])"
                    )
                );

            const currentIndex =
                swatches.indexOf(
                    swatch
                );

            if (currentIndex < 0) {
                return;
            }

            const direction =
                event.key ===
                    "ArrowRight"
                    ? 1
                    : -1;

            const nextIndex =
                (
                    currentIndex +
                    direction +
                    swatches.length
                ) %
                swatches.length;

            swatches[nextIndex].focus();
            this.selectColor(
                swatches[nextIndex]
            );
        }

        /**
         * Selecciona un color relacionado y actualiza todo el card.
         */
        selectColor(swatch) {
            if (
                !swatch ||
                swatch.classList.contains(
                    "is-active"
                )
            ) {
                return;
            }

            this.root
                .querySelectorAll(
                    "[data-nosto-card-swatch]"
                )
                .forEach((item) => {
                    const isActive =
                        item === swatch;

                    item.classList.toggle(
                        "is-active",
                        isActive
                    );

                    item.setAttribute(
                        "aria-pressed",
                        String(isActive)
                    );
                });

            this.updateCardFromSwatch(
                swatch,
                {
                    changeMediaGroup: true,
                    emit: true,
                }
            );
        }

        /**
         * Actualiza los datos visuales y funcionales desde un swatch.
         */
        updateCardFromSwatch(
            swatch,
            {
                changeMediaGroup = true,
                emit = true,
            } = {}
        ) {
            const productId =
                this.normalizeProductId(
                    swatch.dataset
                        .productId
                );

            const productHandle =
                this.normalizeString(
                    swatch.dataset
                        .productHandle
                );

            const productUrl =
                this.normalizeString(
                    swatch.dataset
                        .productUrl
                );

            const quickBuyUrl =
                this.normalizeString(
                    swatch.dataset
                        .quickBuyUrl
                );

            const title =
                this.normalizeString(
                    swatch.dataset.title
                );

            const color =
                this.normalizeString(
                    swatch.dataset.color
                );

            const formattedPrice =
                this.normalizeFormattedPrice(
                    swatch.dataset
                        .formattedPrice
                );

            const formattedComparePrice =
                this.normalizeFormattedPrice(
                    swatch.dataset
                        .formattedComparePrice
                );

            const comparePrice =
                Number(
                    swatch.dataset
                        .comparePrice ||
                    0
                );

            const isAvailable =
                swatch.dataset.available ===
                "true";

            this.root.dataset
                .activeProductId =
                productId;

            this.root.dataset
                .activeHandle =
                productHandle;

            this.root.dataset
                .activeColor =
                color;

            this.updateProductLinks(
                productUrl
            );

            this.updateTitle(title);
            this.updatePrice({
                formattedPrice,
                formattedComparePrice,
                comparePrice,
            });

            this.updateBadge(
                productId
            );

            this.updateQuickAdd({
                productId,
                productHandle,
                productUrl,
                quickBuyUrl,
                isAvailable,
            });

            if (changeMediaGroup) {
                const mediaGroup =
                    this.findMediaGroupByProductId(
                        productId
                    );

                if (mediaGroup) {
                    this.activateMediaGroup(
                        mediaGroup,
                        {
                            resetPosition: true,
                        }
                    );
                }
            }

            if (emit) {
                const detail = {
                    card: this,
                    productId,
                    productHandle,
                    productUrl,
                    color,
                    swatch,
                    context:
                        this.context,
                };

                this.onColorChange?.(
                    detail
                );

                this.root.dispatchEvent(
                    new CustomEvent(
                        "nosto:product-card:color-change",
                        {
                            bubbles: true,
                            detail,
                        }
                    )
                );
            }
        }

        /**
         * Busca el grupo de imágenes de un producto relacionado.
         */
        findMediaGroupByProductId(
            productId
        ) {
            return Array.from(
                this.root.querySelectorAll(
                    "[data-nosto-card-media-group]"
                )
            ).find(
                (group) =>
                    this.normalizeProductId(
                        group.dataset
                            .productId
                    ) === productId
            ) || null;
        }

        /**
         * Actualiza todos los enlaces del card al producto seleccionado.
         */
        updateProductLinks(productUrl) {
            if (!productUrl) {
                return;
            }

            this.root
                .querySelectorAll(
                    [
                        "[data-nosto-card-link]",
                        "[data-nosto-card-media-group] a",
                    ].join(",")
                )
                .forEach((link) => {
                    link.href =
                        this.buildTrackedUrl(
                            productUrl
                        );
                });
        }

        /**
         * Construye la URL del producto incorporando attribution cuando existe.
         */
        buildTrackedUrl(productUrl) {
            if (
                !productUrl ||
                !this.attributionKey
            ) {
                return productUrl;
            }

            try {
                const url =
                    new URL(
                        productUrl,
                        global.location.origin
                    );

                url.searchParams.set(
                    "nosto",
                    this.attributionKey
                        .replace(
                            /^\?nosto=/,
                            ""
                        )
                );

                return (
                    url.pathname +
                    url.search +
                    url.hash
                );
            } catch (error) {
                return productUrl;
            }
        }

        /**
         * Actualiza el título del producto seleccionado.
         */
        updateTitle(title) {
            const titleElement =
                this.root.querySelector(
                    "[data-nosto-card-title]"
                );

            if (
                titleElement &&
                title
            ) {
                titleElement.textContent =
                    title;
            }
        }

        /**
         * Actualiza el precio y precio comparativo.
         */
        updatePrice({
            formattedPrice,
            formattedComparePrice,
            comparePrice,
        }) {
            const currentPriceElement =
                this.root.querySelector(
                    "[data-nosto-card-current-price]"
                );

            const comparePriceElement =
                this.root.querySelector(
                    "[data-nosto-card-compare-price]"
                );

            const hasDiscount =
                comparePrice > 0;

            const normalizedPrice =
                this.normalizeFormattedPrice(
                    formattedPrice
                );

            const normalizedComparePrice =
                this.normalizeFormattedPrice(
                    formattedComparePrice
                );

            if (
                currentPriceElement &&
                normalizedPrice
            ) {
                currentPriceElement.textContent =
                    normalizedPrice;

                currentPriceElement.classList.toggle(
                    "is-sale",
                    hasDiscount
                );
            }

            if (comparePriceElement) {
                comparePriceElement.hidden =
                    !hasDiscount;

                comparePriceElement.textContent =
                    hasDiscount
                        ? normalizedComparePrice
                        : "";
            }
        }

        /**
 * Actualiza el badge según el producto/color activo.
 *
 * La lógica de selección del badge vive exclusivamente en Liquid.
 * JavaScript únicamente recupera el HTML previamente renderizado
 * para el producto seleccionado.
 */
        updateBadge(productId) {
            const badgeContainer =
                this.root.querySelector(
                    "[data-nosto-card-badge]"
                );

            if (!badgeContainer) {
                return;
            }

            if (this.isEditorial()) {
                badgeContainer.hidden = true;
                return;
            }

            const normalizedProductId =
                this.normalizeProductId(
                    productId
                );

            const badgeTemplate =
                Array.from(
                    this.root.querySelectorAll(
                        "[data-nosto-card-badge-template]"
                    )
                ).find((template) => {
                    return (
                        this.normalizeProductId(
                            template.dataset.productId
                        ) === normalizedProductId
                    );
                });

            if (!badgeTemplate) {
                badgeContainer.replaceChildren();
                badgeContainer.hidden = true;

                return;
            }

            badgeContainer.replaceChildren(
                badgeTemplate.content.cloneNode(true)
            );

            badgeContainer.hidden =
                badgeContainer.children.length === 0;
        }
        /**
         * Actualiza el botón que abre la compra rápida.
         */
        updateQuickAdd({
            productId,
            productHandle,
            productUrl,
            quickBuyUrl,
            isAvailable,
        }) {
            const button =
                this.root.querySelector(
                    "[data-nosto-card-quick-add]"
                );

            if (!button) {
                return;
            }

            button.dataset.productId =
                productId;

            button.dataset.productHandle =
                productHandle;

            button.dataset.productUrl =
                productUrl;

            button.dataset.quickBuyUrl =
                quickBuyUrl;

            button.disabled =
                !isAvailable;

            const text =
                button.querySelector(
                    "[data-nosto-card-quick-add-text]"
                );

            if (text) {
                text.textContent =
                    isAvailable
                        ? "Añadir"
                        : "Agotado";
            }
        }

        /**
         * Solicita la apertura del modal de compra rápida.
         */
        openQuickBuy(button) {
            if (
                !button ||
                button.disabled
            ) {
                return;
            }

            const detail = {
                card: this,

                productId:
                    this.normalizeProductId(
                        button.dataset
                            .productId
                    ),

                productHandle:
                    this.normalizeString(
                        button.dataset
                            .productHandle
                    ),

                productUrl:
                    this.normalizeString(
                        button.dataset
                            .productUrl
                    ),

                quickBuyUrl:
                    this.normalizeString(
                        button.dataset
                            .quickBuyUrl
                    ),

                view:
                    this.quickBuyViewName,

                button,

                hit:
                    this.hit,

                source:
                    this.source,

                track:
                    this.track,

                attributionKey:
                    this.attributionKey,

                context:
                    this.context,
            };

            this.onQuickBuyOpen?.(
                detail
            );

            document.dispatchEvent(
                new CustomEvent(
                    "nosto:quick-buy:open",
                    {
                        bubbles: true,
                        detail,
                    }
                )
            );
        }

        /**
 * Inicializa una card cuyo HTML ya fue renderizado por Liquid.
 *
 * No requiere hit, viewService ni una petición a una vista de producto.
 */
        mountExisting(root) {
            if (!(root instanceof Element)) {
                throw new TypeError(
                    'mountExisting() requiere el elemento raíz del product card.'
                );
            }

            if (root.__nostoProductCardInstance) {
                return root.__nostoProductCardInstance;
            }

            this.root = root;
            this.container = root.parentElement;

            this.productId = this.normalizeProductId(
                root.dataset.activeProductId ||
                root.dataset.productId ||
                this.productId
            );

            this.cardType = this.normalizeCardType(
                root.dataset.cardType ||
                this.cardType
            );

            this.applyCardType();
            this.initialize();

            this.isRendered = true;
            this.isDestroyed = false;

            root.__nostoProductCardInstance = this;

            return this;
        }

        /**
 * Indica si el card está utilizando la vista editorial.
 */
        isEditorial() {
            return this.cardType === "editorial";
        }

        /**
         * Destruye todos los carruseles existentes del card.
         */
        destroyCarousels() {
            for (
                const instance of
                this.flickityByGroup.values()
            ) {
                try {
                    instance?.destroy();
                } catch (error) {
                    this.log(
                        "No fue posible destruir una instancia de Flickity.",
                        error
                    );
                }
            }

            this.flickityByGroup.clear();
            this.activeFlickity = null;
        }

        /**
 * Deja el media del card en modo editorial.
 *
 * Si Flickity ya existe no alteramos físicamente sus slides:
 * solamente seleccionamos el primero y deshabilitamos interacción.
 *
 * Si Flickity todavía no existe, ocultamos los slides posteriores
 * para soportar una carga inicial directamente en Editorial.
 */
        setStaticMediaGroup(
            group,
            {
                hasFlickity = false,
            } = {}
        ) {
            if (!group) {
                return;
            }

            const slides =
                this.getSlides(group);

            const initialIndex =
                this.getInitialMediaIndex(group);

            slides.forEach(
                (slide, index) => {
                    const isSelected =
                        index === initialIndex;

                    /*
                     * Si Flickity no existe, mostramos físicamente
                     * únicamente la imagen configurada.
                     */
                    if (!hasFlickity) {
                        slide.hidden =
                            !isSelected;
                    }

                    slide.setAttribute(
                        "aria-hidden",
                        String(!isSelected)
                    );

                    slide
                        .querySelectorAll(
                            "a, button"
                        )
                        .forEach(
                            (element) => {
                                element.tabIndex =
                                    isSelected
                                        ? 0
                                        : -1;
                            }
                        );
                }
            );

            const previousButton =
                this.root?.querySelector(
                    "[data-nosto-card-prev]"
                );

            const nextButton =
                this.root?.querySelector(
                    "[data-nosto-card-next]"
                );

            if (previousButton) {
                previousButton.hidden = true;

                previousButton.setAttribute(
                    "aria-hidden",
                    "true"
                );
            }

            if (nextButton) {
                nextButton.hidden = true;

                nextButton.setAttribute(
                    "aria-hidden",
                    "true"
                );
            }

            group
                .querySelectorAll(
                    "[data-nosto-card-pagination]"
                )
                .forEach((pagination) => {
                    pagination.hidden = true;

                    pagination.setAttribute(
                        "aria-hidden",
                        "true"
                    );
                });

            this.updatePagination(
                group,
                initialIndex
            );
        }

        /**
         * Restaura los slides que pudieron haberse ocultado
         * mientras el card estaba en vista editorial.
         */
        restoreMediaGroupSlides(group) {
            if (!group) {
                return;
            }

            this.getSlides(group)
                .forEach((slide) => {
                    slide.hidden = false;
                });
        }

        /**
 * Sincroniza el comportamiento del media según
 * el tipo actual de card.
 *
 * Flickity no se destruye al pasar a Editorial.
 * Mantener la instancia evita el flash visual al volver a Normal.
 */
        syncMediaMode({
            resetPosition = true,
        } = {}) {
            if (!this.activeMediaGroup) {
                return null;
            }

            /*
             * EDITORIAL
             */
            if (this.isEditorial()) {
                const instance =
                    this.flickityByGroup.get(
                        this.activeMediaGroup
                    );

                /*
                 * Si la card venía de Normal ya existe Flickity.
                 * Lo dejamos montado pero completamente inmóvil.
                 */
                if (instance) {
                    this.activeFlickity =
                        instance;

                    const initialIndex =
                        this.getInitialMediaIndex(
                            this.activeMediaGroup
                        );

                    instance.select(
                        initialIndex,
                        false,
                        true
                    );

                    /*
                     * Flickity expone estos métodos en la implementación
                     * draggable. Evitamos swipe/drag sin destruir la instancia.
                     */
                    instance.unbindDrag?.();

                    this.setStaticMediaGroup(
                        this.activeMediaGroup,
                        {
                            hasFlickity: true,
                        }
                    );

                    return instance;
                }

                /*
                 * Si la página cargó directamente en Editorial,
                 * todavía no existe Flickity.
                 */
                this.activeFlickity = null;

                this.setStaticMediaGroup(
                    this.activeMediaGroup,
                    {
                        hasFlickity: false,
                    }
                );

                return null;
            }

            /*
             * NORMAL
             */
            this.restoreMediaGroupSlides(
                this.activeMediaGroup
            );

            let instance =
                this.flickityByGroup.get(
                    this.activeMediaGroup
                );

            /*
             * Solo lo creamos si todavía no existe.
             *
             * Editorial -> Normal reutilizará la misma instancia.
             */
            if (!instance) {
                instance =
                    this.createFlickity(
                        this.activeMediaGroup
                    );
            }

            this.activeFlickity =
                instance;

            if (!instance) {
                this.updateCarouselControls(
                    this.activeMediaGroup,
                    null
                );

                return null;
            }

            /*
             * Reactivamos drag si la instancia venía de Editorial.
             */
            instance.bindDrag?.();

            requestAnimationFrame(() => {
                if (
                    !this.activeFlickity ||
                    this.isEditorial()
                ) {
                    return;
                }

                instance.resize();
                instance.reposition?.();

                if (resetPosition) {
                    const initialIndex =
                        this.getInitialMediaIndex(
                            this.activeMediaGroup
                        );

                    instance.select(
                        initialIndex,
                        false,
                        true
                    );
                }

                this.updateCarouselControls(
                    this.activeMediaGroup,
                    instance
                );

                this.handleFlickitySelect(
                    this.activeMediaGroup,
                    instance
                );

                this.playMobileSwipeHint();
            });

            return instance;
        }

        /**
         * Recalcula el carrusel cuando una card vuelve a ser visible.
         */
        refresh() {
            if (!this.root) {
                return;
            }

            const activeGroup =
                this.getActiveMediaGroup() ||
                this.activeMediaGroup;

            if (!activeGroup) {
                return;
            }

            if (!this.activeFlickity) {
                this.activateMediaGroup(activeGroup, {
                    resetPosition: false,
                });

                return;
            }

            requestAnimationFrame(() => {
                this.activeFlickity?.resize();

                requestAnimationFrame(() => {
                    this.activeFlickity?.reposition?.();
                });
            });
        }

        /**
 * Elimina espacios entre el símbolo $ y el importe.
 *
 * Soporta espacios normales y non-breaking spaces
 * provenientes del formatter de moneda de Shopify.
 */
        normalizeFormattedPrice(value) {
            return this.normalizeString(value)
                .replace(/\$\s+/g, "$");
        }

        /**
 * Registra el clic sobre el producto mediante el callback configurado.
 */
        recordProductClick(link) {
            const productId =
                this.normalizeProductId(
                    this.root.dataset
                        .activeProductId ||
                    this.productId
                );

            const detail = {
                card: this,
                productId,
                link,
                hit:
                    this.hit,
                source:
                    this.source,
                track:
                    this.track,
                context:
                    this.context,
            };

            /*
             * Esto conserva tu tracking actual:
             * category / serp / autocomplete.
             */
            this.onProductClick?.(
                detail
            );

            this.root.dispatchEvent(
                new CustomEvent(
                    "nosto:product-card:click",
                    {
                        bubbles: true,
                        detail,
                    }
                )
            );
        }

        applyTrackingToExistingLinks() {
            if (!this.root || !this.attributionKey) {
                return;
            }

            this.root
                .querySelectorAll(
                    [
                        "[data-nosto-card-link]",
                        "[data-nosto-card-media-group] a",
                    ].join(",")
                )
                .forEach((link) => {
                    const href =
                        link.getAttribute("href");

                    if (!href) {
                        return;
                    }

                    link.href =
                        this.buildTrackedUrl(href);
                });
        }

        /**
         * Recalcula el carrusel activo después de un cambio de ancho.
         */
        handleResize() {
            this.activeFlickity
                ?.resize();
        }

        /**
         * Activa o desactiva el estado visual de carga.
         */
        setLoading(isLoading) {
            this.container?.classList.toggle(
                "is-loading",
                Boolean(isLoading)
            );

            this.container?.setAttribute(
                "aria-busy",
                String(
                    Boolean(isLoading)
                )
            );
        }

        /**
         * Muestra un estado mínimo cuando no fue posible cargar el card.
         */
        renderError(error) {
            console.error(
                `[NostoProductCard] No fue posible renderizar el producto ${this.productId}.`,
                error
            );

            if (!this.container) {
                return;
            }

            const fallbackLink =
                document.createElement("a");

            fallbackLink.className =
                "nosto-product-card__error";

            fallbackLink.href =
                this.hit?.url || "#";

            fallbackLink.textContent =
                this.hit?.name ||
                "No fue posible cargar el producto.";

            this.container.replaceChildren(
                fallbackLink
            );
        }

        /**
         * Destruye carruseles y elimina eventos de la instancia.
         */
        destroy() {
            if (this.isDestroyed) {
                return;
            }

            this.root?.removeEventListener(
                "click",
                this.boundHandleClick
            );

            this.root?.removeEventListener(
                "keydown",
                this.boundHandleKeydown
            );

            document.removeEventListener(
                "theme:resize:width",
                this.boundHandleResize
            );

            if (
                this.swipeHintTimeout
            ) {
                global.clearTimeout(
                    this.swipeHintTimeout
                );

                this.swipeHintTimeout =
                    null;
            }

            if (
                this.swipeHintAnimationFrame
            ) {
                cancelAnimationFrame(
                    this.swipeHintAnimationFrame
                );

                this.swipeHintAnimationFrame =
                    null;
            }

            for (
                const instance of
                this.flickityByGroup.values()
            ) {
                try {
                    instance?.destroy();
                } catch (error) {
                    this.log(
                        "No fue posible destruir una instancia de Flickity.",
                        error
                    );
                }
            }

            this.flickityByGroup.clear();

            this.activeFlickity = null;
            this.activeMediaGroup = null;
            this.swipeHintHasPlayed = false;
            this.swipeHintAnimationFrame = null;
            this.swipeHintTimeout = null;
            this.root = null;

            this.isRendered = false;
            this.isDestroyed = true;
        }
    }

    const CARD_SELECTOR =
        '[data-nosto-product-card]';

    const CARD_INSTANCE_KEY =
        '__nostoProductCardInstance';

    const observedCards =
        new WeakSet();

    let cardResizeObserver = null;
    let cardIntersectionObserver = null;
    let cardMutationObserver = null;
    let globalInitializerStarted = false;

    /**
     * Comprueba si un elemento puede medirse.
     *
     * Un card dentro de un panel con hidden no debe inicializar Flickity
     * todavía, porque su ancho será cero.
     */
    function isCardMeasurable(cardRoot) {
        if (!(cardRoot instanceof Element)) {
            return false;
        }

        if (!cardRoot.isConnected) {
            return false;
        }

        if (cardRoot.closest('[hidden]')) {
            return false;
        }

        const styles =
            global.getComputedStyle(cardRoot);

        if (
            styles.display === 'none' ||
            styles.visibility === 'hidden'
        ) {
            return false;
        }

        const rect =
            cardRoot.getBoundingClientRect();

        return (
            rect.width > 0 &&
            rect.height > 0
        );
    }

    /**
     * Crea o recupera la instancia de una card existente.
     */
    function getOrCreateCardInstance(cardRoot) {
        if (!(cardRoot instanceof Element)) {
            return null;
        }

        let instance =
            cardRoot[CARD_INSTANCE_KEY];

        if (instance) {
            return instance;
        }

        instance = new NostoProductCard({
            productId:
                cardRoot.dataset.activeProductId ||
                cardRoot.dataset.productId ||
                '',

            container:
                cardRoot.parentElement,

            cardType:
                cardRoot.dataset.cardType ||
                'normal',

            source:
                cardRoot.dataset.nostoSource ||
                'liquid-dom',

            track:
                cardRoot.dataset.nostoTrack ||
                '',

            attributionKey:
                cardRoot.dataset.nostoAttribution ||
                '',

            context: {
                source:
                    cardRoot.dataset.nostoSource ||
                    'liquid-dom',

                track:
                    cardRoot.dataset.nostoTrack ||
                    '',

                attributionKey:
                    cardRoot.dataset.nostoAttribution ||
                    '',
            },
        });

        instance.mountExisting(cardRoot);

        return instance;
    }

    /**
     * Inicializa o recalcula una card cuando ya es visible.
     */
    function initializeCard(cardRoot) {
        if (!isCardMeasurable(cardRoot)) {
            return null;
        }

        try {
            const instance =
                getOrCreateCardInstance(cardRoot);

            instance?.refresh();

            return instance;
        } catch (error) {
            console.error(
                '[NostoProductCard] No fue posible inicializar una card existente.',
                {
                    cardRoot,
                    error,
                }
            );

            return null;
        }
    }

    /**
     * Registra observers sobre una card.
     */
    function observeCard(cardRoot) {
        if (
            !(cardRoot instanceof Element) ||
            observedCards.has(cardRoot)
        ) {
            return;
        }

        observedCards.add(cardRoot);

        cardResizeObserver?.observe(cardRoot);
        cardIntersectionObserver?.observe(cardRoot);

        if (isCardMeasurable(cardRoot)) {
            initializeCard(cardRoot);
        }
    }

    /**
     * Localiza cards dentro de un nodo o el propio nodo.
     */
    function findCards(scope) {
        if (
            !(scope instanceof Element) &&
            !(scope instanceof Document) &&
            !(scope instanceof DocumentFragment)
        ) {
            return [];
        }

        const cards = [];

        if (
            scope instanceof Element &&
            scope.matches(CARD_SELECTOR)
        ) {
            cards.push(scope);
        }

        cards.push(
            ...scope.querySelectorAll(
                CARD_SELECTOR
            )
        );

        return Array.from(
            new Set(cards)
        );
    }

    /**
     * Inicializador público.
     *
     * Puede seguir usándose manualmente, aunque normalmente los observers
     * se encargarán de todo.
     */
    global.initializeExistingNostoProductCards = (
        scope = document
    ) => {
        const cards =
            findCards(scope);

        cards.forEach(observeCard);

        return cards
            .map((cardRoot) =>
                initializeCard(cardRoot)
            )
            .filter(Boolean);
    };

    /**
     * Recalcula cards dentro de un contenedor que acaba de mostrarse.
     */
    global.refreshExistingNostoProductCards = (
        scope = document
    ) => {
        const cards =
            findCards(scope);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                cards.forEach((cardRoot) => {
                    observeCard(cardRoot);
                    initializeCard(cardRoot);
                });
            });
        });
    };

    /**
     * Inicia la observación global una sola vez.
     */
    function startGlobalProductCardInitializer() {
        if (globalInitializerStarted) {
            return;
        }

        globalInitializerStarted = true;

        /*
         * Cuando cambia el tamaño real de una card, Flickity recalcula
         * viewport, slides y posición.
         */
        if ('ResizeObserver' in global) {
            cardResizeObserver =
                new ResizeObserver((entries) => {
                    entries.forEach((entry) => {
                        const cardRoot =
                            entry.target;

                        if (!isCardMeasurable(cardRoot)) {
                            return;
                        }

                        const instance =
                            cardRoot[CARD_INSTANCE_KEY];

                        if (instance) {
                            instance.refresh();
                        } else {
                            initializeCard(cardRoot);
                        }
                    });
                });
        }

        /*
         * Cuando una card entra al viewport se inicializa.
         *
         * Esto evita inicializar anticipadamente cards de tabs ocultos,
         * carruseles lejanos o contenido debajo del fold.
         */
        if ('IntersectionObserver' in global) {
            cardIntersectionObserver =
                new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (
                                !entry.isIntersecting &&
                                entry.intersectionRatio <= 0
                            ) {
                                return;
                            }

                            initializeCard(
                                entry.target
                            );
                        });
                    },
                    {
                        rootMargin:
                            '300px 0px 300px 0px',
                        threshold: 0.01,
                    }
                );
        }

        /*
         * Detecta:
         * - cards insertados mediante AJAX;
         * - cards añadidos por Nosto;
         * - panels que dejan de tener hidden;
         * - tabs que cambian clases o estilos.
         */
        cardMutationObserver =
            new MutationObserver((mutations) => {
                const scopesToRefresh =
                    new Set();

                mutations.forEach((mutation) => {
                    if (
                        mutation.type === 'childList'
                    ) {
                        mutation.addedNodes.forEach(
                            (node) => {
                                if (
                                    node instanceof Element ||
                                    node instanceof DocumentFragment
                                ) {
                                    findCards(node)
                                        .forEach(observeCard);
                                }
                            }
                        );

                        return;
                    }

                    if (
                        mutation.type === 'attributes'
                    ) {
                        const target =
                            mutation.target;

                        if (!(target instanceof Element)) {
                            return;
                        }

                        /*
                         * Si el atributo cambió en la propia card o en uno de sus
                         * padres, buscamos cards descendientes y las refrescamos.
                         */
                        if (
                            target.matches(CARD_SELECTOR) ||
                            target.querySelector(CARD_SELECTOR)
                        ) {
                            scopesToRefresh.add(target);
                        }
                    }
                });

                if (!scopesToRefresh.size) {
                    return;
                }

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        scopesToRefresh.forEach(
                            (scope) => {
                                global
                                    .refreshExistingNostoProductCards(
                                        scope
                                    );
                            }
                        );
                    });
                });
            });

        cardMutationObserver.observe(
            document.documentElement,
            {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: [
                    'hidden',
                    'class',
                    'style',
                    'open',
                ],
            }
        );

        global
            .initializeExistingNostoProductCards(
                document
            );
    }

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            startGlobalProductCardInitializer,
            {
                once: true,
            }
        );
    } else {
        startGlobalProductCardInitializer();
    }

    global.NostoProductCard =
        NostoProductCard;
})(window);